import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/layout/Layout";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { MarketplaceLogo, MarketplaceBadge } from "../components/MarketplaceLogo";
import { fetchWithRetry } from "../utils/fetchWithRetry";
import {
  Package,
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  ExternalLink,
  Edit2,
  Image,
  AlertTriangle,
  RefreshCw,
  AlertCircle,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CATEGORIES = [
  "Electronics",
  "Fashion",
  "Home & Garden",
  "Sports & Outdoors",
  "Beauty & Health",
  "Toys & Games",
  "Automotive",
  "Books & Media",
  "General",
  "Other",
];

export default function MyCatalogPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [fetchError, setFetchError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  // Edit modal
  const [editProduct, setEditProduct] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editImages, setEditImages] = useState([]);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchProducts = useCallback(async (isManualRetry = false) => {
    if (!isManualRetry) {
      setLoading(true);
    }
    setFetchError(null);
    setRetryCount(0);
    setIsRetrying(false);
    
    try {
      const result = await fetchWithRetry(`${API}/products/mine`, {
        withCredentials: true,
        maxRetries: 3,
        timeout: 25000,
        initialDelay: 1000,
        onRetry: (attempt) => {
          setRetryCount(attempt);
          setIsRetrying(true);
        },
      });

      if (result.success) {
        setProducts(result.data.products || []);
        setTotal(result.data.total || 0);
        setFetchError(null);
      } else {
        // Failure - set error with details
        setFetchError({
          code: result.errorCode || 'UNKNOWN',
          status: result.status || 0,
          message: result.error?.message || 'Unable to reach server',
        });
        setProducts([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setFetchError({
        code: error.code || 'EXCEPTION',
        status: 0,
        message: error.message,
      });
      setProducts([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setIsRetrying(false);
      setFetchAttempted(true);
      setRetryCount(0);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchProducts();
    } else {
      setLoading(false);
    }
  }, [user, fetchProducts]);

  const openEditModal = (product) => {
    setEditProduct(product);
    setEditTitle(product.title || product.name || "");
    setEditCategory(product.category || "General");
    setEditImages(product.images || []);
  };

  const closeEditModal = () => {
    setEditProduct(null);
    setEditTitle("");
    setEditCategory("");
    setEditImages([]);
    setNewImageUrl("");
  };

  const handleAddImage = () => {
    if (newImageUrl.trim()) {
      setEditImages([...editImages, newImageUrl.trim()]);
      setNewImageUrl("");
    }
  };

  const handleRemoveImage = (index) => {
    setEditImages(editImages.filter((_, i) => i !== index));
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) {
      toast.error("Title is required");
      return;
    }

    setSaving(true);
    try {
      await axios.patch(
        `${API}/products/${editProduct.product_id}`,
        {
          title: editTitle.trim(),
          category: editCategory,
          images: editImages,
        },
        { withCredentials: true }
      );
      toast.success("Product updated");
      closeEditModal();
      fetchProducts();
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error(error.response?.data?.detail || "Failed to update product");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending_review":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <Clock className="h-3 w-3 mr-1" />
            Pending Review
          </Badge>
        );
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!user) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Sign in Required</h1>
          <p className="text-slate-600 mb-6">Please sign in to view your catalog.</p>
          <Button onClick={() => navigate("/login")}>Sign In</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="catalog-title">
              <Package className="inline h-8 w-8 mr-3" />
              My Catalog
            </h1>
            <p className="text-slate-600 mt-1">
              {total} product{total !== 1 ? "s" : ""} in your catalog
            </p>
          </div>
          <Button onClick={() => navigate("/submit")} data-testid="submit-new-btn">
            <Plus className="h-4 w-4 mr-2" />
            Submit Product
          </Button>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 inline mr-2" />
          <strong>Reference Only:</strong> Product images and descriptions are provided for reference 
          only. CheepSweepz is not affiliated with or endorsed by any marketplace.
        </div>

        {loading ? (
          <div>
            {isRetrying && (
              <div className="text-center py-4 mb-4 bg-orange-50 rounded-lg border border-orange-200">
                <div className="flex items-center justify-center gap-2 text-orange-700">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="font-medium">
                    Waking servers... (attempt {retryCount}/3)
                  </span>
                </div>
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-64" />
              ))}
            </div>
          </div>
        ) : fetchError ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200" data-testid="catalog-error-state">
            <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              Cannot reach server
            </h2>
            <p className="text-slate-600 mb-6">
              Unable to load your catalog. Please try again.
            </p>
            <Button onClick={() => fetchProducts(true)} data-testid="catalog-retry-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Tap to Retry
            </Button>
          </div>
        ) : products.length === 0 && fetchAttempted ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              No Products Yet
            </h2>
            <p className="text-slate-600 mb-6">
              Start by submitting a product URL from any supported marketplace.
            </p>
            <Button onClick={() => navigate("/submit")}>
              <Plus className="h-4 w-4 mr-2" />
              Submit Your First Product
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product.product_id}
                className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`product-${product.product_id}`}
              >
                {/* Image */}
                <div className="aspect-video bg-slate-100 relative">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.title || product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="h-12 w-12 text-slate-300" />
                    </div>
                  )}
                  {/* Marketplace Badge */}
                  <div className="absolute top-2 left-2">
                    <MarketplaceBadge marketplace={product.marketplace || product.source_platform} size="xs" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-slate-900 line-clamp-2">
                      {product.title || product.name}
                    </h3>
                    {getStatusBadge(product.approval_status)}
                  </div>

                  <p className="text-xs text-slate-500 mb-3">
                    {product.category || "General"}
                  </p>

                  {product.rejection_notes && (
                    <div className="bg-red-50 rounded p-2 text-xs text-red-700 mb-3">
                      <strong>Rejected:</strong> {product.rejection_notes}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {product.source_url && (
                      <a
                        href={product.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-600 hover:underline flex items-center"
                      >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        View Original
                      </a>
                    )}
                  </div>

                  {/* Edit button - only for non-approved products */}
                  {product.approval_status !== "approved" && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-3"
                      onClick={() => openEditModal(product)}
                      data-testid={`edit-${product.product_id}`}
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Modal */}
        <Dialog open={!!editProduct} onOpenChange={closeEditModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Product</DialogTitle>
            </DialogHeader>

            {editProduct && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-title">Title</Label>
                  <Input
                    id="edit-title"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    data-testid="edit-title-input"
                  />
                </div>

                <div>
                  <Label htmlFor="edit-category">Category</Label>
                  <Select value={editCategory} onValueChange={setEditCategory}>
                    <SelectTrigger data-testid="edit-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Images</Label>
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {editImages.map((img, idx) => (
                      <div
                        key={idx}
                        className="relative aspect-square bg-slate-100 rounded overflow-hidden group"
                      >
                        <img src={img} alt="" className="w-full h-full object-cover" />
                        <button
                          onClick={() => handleRemoveImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs opacity-0 group-hover:opacity-100"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder="Add image URL"
                      className="flex-1"
                    />
                    <Button variant="outline" onClick={handleAddImage} type="button">
                      Add
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-slate-500">
                  Note: You can only edit title, category, and images. Pricing is set by admin.
                </p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={closeEditModal}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-btn">
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
